import type { MessageKey } from '../i18n/messages';
import { nationalDigits, type CountryConfig } from './countryConfig.ts';

/**
 * Per-country phone number rules for the auth screens.
 *
 * The national number format is genuinely country-specific — Brazil has two-digit
 * area codes plus a mandatory leading 9 on mobiles, India has ten digits starting
 * 6-9, and neither rule describes the other. Keeping these as one BR-shaped
 * function meant a second market would either inherit Brazil's validation
 * (rejecting valid local numbers) or need it stripped out.
 *
 * A country with no entry gets {@link GENERIC_PLAN}: length-only validation and
 * no input mask. That is deliberately permissive — a wrong mask is worse than
 * none, because it silently mangles what the player typed.
 */

export type PhoneHint = { key: MessageKey; params?: Record<string, string> } | null;

export type PhonePlan = {
  /** Longest national number, used to cap input. */
  maxDigits: number;
  /** Digits-only national number, calling code stripped, capped. */
  normalize(raw: string, country: CountryConfig): string;
  /** Display mask. Returns the digits unchanged when the country has no mask. */
  format(raw: string, country: CountryConfig): string;
  isValid(raw: string, country: CountryConfig): boolean;
  /**
   * Stricter than {@link isValid}: true only for a number that can receive an
   * SMS. `isValid` also accepts a Brazilian landline (10 digits, subscriber
   * digit 2-5) because login and forgot-password must keep working for
   * pre-existing accounts that registered with one before OTP registration
   * existed. Registration is SMS-only end to end (design doc §3.1), so it
   * needs the narrower check — a landline that passes `isValid` still cannot
   * receive the code, and would otherwise pass the client-side gate only to
   * fail at the backend's `BR_PHONE` (13 E.164 digits, mobile shape) as
   * `INVALID_IDENTIFIER`.
   */
  isMobile(raw: string, country: CountryConfig): boolean;
  /**
   * What is wrong right now, or null when the number is fine (or still being
   * typed). `hint` is shared by every auth screen — login and forgot-password
   * must keep calling out a Brazilian landline as valid, since pre-existing
   * accounts can hold one (see `isMobile`'s doc comment). Pass
   * `{ mobileOnly: true }` only from a context where `isMobile` is also the
   * submit gate (registration): it adds a hint for the one shape `isValid`
   * accepts but `isMobile` does not — a 10-digit landline with a legal DDD —
   * so the player is told to enter a mobile number instead of just seeing the
   * field turn red with no explanation.
   */
  hint(raw: string, country: CountryConfig, opts?: { mobileOnly?: boolean }): PhoneHint;
  /**
   * The line shown under an empty or half-typed phone field before `hint` has
   * anything specific to say. Brazil describes its DDD + 9 shape; a country
   * without local rules gets the neutral "make sure it is correct" copy. Lives
   * on the plan so a screen never has to know which country has a special
   * format — the verify-phone sheet used to show Brazil's DDD sentence to
   * Argentine players for exactly that reason.
   */
  introHint: MessageKey;
};

/** Brazilian area codes (DDD). Not a contiguous range — 16, 20, 23 and others are unassigned. */
const BR_VALID_DDDS = new Set([
  11, 12, 13, 14, 15, 16, 17, 18, 19,
  21, 22, 24,
  27, 28,
  31, 32, 33, 34, 35, 37, 38,
  41, 42, 43, 44, 45, 46,
  47, 48, 49,
  51, 53, 54, 55,
  61, 62, 63, 64, 65, 66, 67, 68, 69,
  71, 73, 74, 75, 77, 79,
  81, 82, 83, 84, 85, 86, 87, 88, 89,
  91, 92, 93, 94, 95, 96, 97, 98, 99,
]);

const BR_PLAN: PhonePlan = {
  maxDigits: 11,

  normalize(raw, country) {
    // Only strip a leading 55 when what remains could still be a full national
    // number. '5511987654321' is prefixed; '5511' is a São Paulo landline that
    // happens to start with the calling code.
    const digits = String(raw || '').replace(/\D/g, '');
    const stripped = digits.length > 11 ? nationalDigits(digits, country) : digits;
    return stripped.slice(0, 11);
  },

  format(raw, country) {
    // Local convention: `(DD) 9XXXX-XXXX` for a mobile, `(DD) XXXX-XXXX` for a
    // landline. The subscriber digit decides the split as soon as it is typed
    // (a mobile always starts with 9), so the mask never jumps from 4-4 to 5-4
    // on the last keystroke the way a length-only mask does.
    const digits = BR_PLAN.normalize(raw, country);
    if (digits.length === 0) return '';
    if (digits.length === 1) return `(${digits}`;
    if (digits.length === 2) return `(${digits}) `;
    const ddd = digits.slice(0, 2);
    const subscriber = digits.slice(2);
    const head = subscriber[0] === '9' ? 5 : 4;
    if (subscriber.length <= head) return `(${ddd}) ${subscriber}`;
    return `(${ddd}) ${subscriber.slice(0, head)}-${subscriber.slice(head)}`;
  },

  isValid(raw, country) {
    const digits = BR_PLAN.normalize(raw, country);
    if (digits.length !== 10 && digits.length !== 11) return false;
    if (!BR_VALID_DDDS.has(Number(digits.slice(0, 2)))) return false;
    const subscriberFirst = Number(digits[2]);
    // 11 digits is a mobile and must start with 9; 10 digits is a landline, whose
    // first subscriber digit is 2-5.
    return digits.length === 11 ? subscriberFirst === 9 : subscriberFirst >= 2 && subscriberFirst <= 5;
  },

  isMobile(raw, country) {
    // Same DDD/shape check as isValid, minus the 10-digit landline branch.
    const digits = BR_PLAN.normalize(raw, country);
    if (digits.length !== 11) return false;
    if (!BR_VALID_DDDS.has(Number(digits.slice(0, 2)))) return false;
    return Number(digits[2]) === 9;
  },

  hint(raw, country, opts) {
    const digits = BR_PLAN.normalize(raw, country);
    if (!digits) return null;
    // A bare 9-digit mobile is the classic mistake: the player typed their number
    // without the area code.
    if (digits.length === 9 && digits[0] === '9') return { key: 'phoneHintMissingDDD' };
    if (digits.length < 10) return { key: 'phoneHintShort', params: { '%n%': String(digits.length) } };
    if (!BR_VALID_DDDS.has(Number(digits.slice(0, 2)))) return { key: 'phoneHintBadDDD' };
    if (digits.length === 11 && digits[2] !== '9') return { key: 'phoneHintMobile9' };
    // A 10-digit number with a legal DDD and subscriber digit 2-5 is a valid
    // landline per `isValid` — but `isMobile` (registration's actual gate)
    // rejects it, since only a mobile can receive the OTP SMS. Without this,
    // a landline typed into the register screen passes every check above and
    // falls through to `null`, so the field just turns red with no reason
    // (mutation of this branch is what the phonePlan.test.ts case pins).
    if (opts?.mobileOnly && digits.length === 10) {
      const subscriberFirst = Number(digits[2]);
      if (subscriberFirst >= 2 && subscriberFirst <= 5) return { key: 'phoneHintNeedsMobile' };
    }
    return null;
  },

  introHint: 'verifyBrHint',
};

/**
 * Fallback for a country with no specific plan.
 *
 * Length only, and no mask. E.164 allows up to 15 digits including the calling
 * code, so 4-14 national digits covers every real number while still catching an
 * obviously truncated one. Argentina and Colombia use this until someone with
 * local knowledge writes their rules — a half-remembered validator that rejects
 * valid numbers is worse than a permissive one, since the player has no way to
 * argue with it.
 */
const GENERIC_PLAN: PhonePlan = {
  maxDigits: 14,

  normalize(raw, country) {
    return nationalDigits(raw, country).slice(0, 14);
  },

  format(raw, country) {
    return GENERIC_PLAN.normalize(raw, country);
  },

  isValid(raw, country) {
    const digits = GENERIC_PLAN.normalize(raw, country);
    return digits.length >= 4 && digits.length <= 14;
  },

  // No local rules to tell a mobile from a landline (same reasoning as the
  // missing mask, see the module doc comment), so this stays as permissive
  // as isValid rather than guessing and rejecting valid mobiles.
  isMobile(raw, country) {
    return GENERIC_PLAN.isValid(raw, country);
  },

  // No hint while typing: `phoneHintShort` reads "n/11 digits", and 11 is
  // Brazil's length, not this country's. A generic plan does not know the
  // target length, so it has nothing truthful to say until the number is
  // obviously complete or obviously not (`isValid` handles both).
  hint() {
    return null;
  },

  introHint: 'phoneTip',
};

const PLANS: Record<string, PhonePlan> = { BR: BR_PLAN };

export function phonePlan(country: CountryConfig): PhonePlan {
  return PLANS[country.code] || GENERIC_PLAN;
}

/**
 * A stored number as the player should read it back: `+54 91123456789`,
 * `+55 (11) 98765-4321`. Strips the country's own calling code if `raw` is
 * E.164 and applies the country's mask, so it is the inverse of `e164Digits`.
 * Empty in, empty out.
 *
 * Replaces a `formatBrazilPhone` that lived in MePage and printed every account's
 * number as `+55 (..)` — an Argentine `+54 9…` came out as `+55 (54) 9…`.
 */
export function displayPhone(raw: string, country: CountryConfig): string {
  const national = phonePlan(country).format(raw, country);
  return national ? `+${country.phonePrefix} ${national}` : '';
}
